<template>
  <div class="messages-page">
    <!-- Announcements Section (Admin Only) -->
    <div v-if="loginStore.isAdmin" class="announcements-section">
      <div class="announcements-header" @click="announcementsExpanded = !announcementsExpanded">
        <div class="announcements-title">
          <i class="fa-solid fa-bullhorn"></i>
          <span>Announcements</span>
        </div>
        <i :class="['fa-solid', announcementsExpanded ? 'fa-chevron-up' : 'fa-chevron-down']"></i>
      </div>

      <div v-if="announcementsExpanded" class="announcements-body">
        <!-- Create Announcement Form -->
        <div class="announcement-form">
          <h4>Create Announcement</h4>
          <div class="form-row">
            <el-input
              v-model="newAnnouncement.subject"
              placeholder="Subject"
              size="small"
            />
          </div>
          <div class="form-row">
            <el-input
              v-model="newAnnouncement.body"
              type="textarea"
              :rows="3"
              placeholder="Announcement body..."
            />
          </div>
          <div class="form-row form-row-inline">
            <AIVSelect
              v-model="newAnnouncement.delivery_method"
              :options="deliveryMethodOptions"
              :searchable="false"
              :clearable="false"
              placeholder="Delivery Method"
              :customStyle="{ width: '200px' }"
            />
            <AIVSelect
              v-model="newAnnouncement.target_user_types"
              :options="userTypeSelectOptions"
              :multiSelect="true"
              :searchable="false"
              placeholder="Target User Types"
              :customStyle="{ flex: '1' }"
            />
            <el-button
              type="primary"
              size="small"
              :loading="sendingAnnouncement"
              :disabled="!canSendAnnouncement"
              @click="sendAnnouncement"
            >
              <i class="fa-solid fa-paper-plane"></i> Send
            </el-button>
          </div>
        </div>

        <!-- Past Announcements Table -->
        <div class="past-announcements">
          <h4>Recent Announcements</h4>
          <AIVTable
            v-if="announcements.length > 0"
            :columns="announcementColumns"
            :data="recentAnnouncements"
            :pagination="{ total: 0, totalPages: 1, currentPage: 1, perPage: recentAnnouncements.length }"
            :item-name="'announcements'"
          >
            <template #cell-senderName="{ row }">
              {{ row.sender ? `${row.sender.first_name} ${row.sender.last_name}` : 'Unknown' }}
            </template>
            <template #cell-target_user_types="{ row }">
              {{ (Array.isArray(row.target_user_types) ? row.target_user_types : JSON.parse(row.target_user_types || '[]')).join(', ') }}
            </template>
            <template #cell-sent_at="{ row }">
              {{ row.sent_at ? new Date(row.sent_at).toLocaleDateString() : '—' }}
            </template>
          </AIVTable>
          <div v-else-if="!loadingAnnouncements" class="text-center py-4">
            <p>No announcements yet.</p>
          </div>
        </div>
      </div>
    </div>

    <div class="messages-layout">
      <!-- Conversation List Sidebar -->
      <div class="conversations-sidebar" :class="{ 'mobile-hidden': showThread && isMobile }">
        <ConversationList
          :conversations="messagesStore.sortedConversations"
          :active-conversation-id="messagesStore.activeConversation?.id"
          :loading="messagesStore.loading"
          @select="selectConversation"
          @new-conversation="showNewConversationModal = true"
        />
      </div>

      <!-- Message Thread Area -->
      <div class="thread-area" :class="{ 'mobile-hidden': !showThread && isMobile }">
        <div v-if="!messagesStore.activeConversation" class="no-conversation-selected">
          <div class="empty-state">
            <i class="fa-regular fa-comments"></i>
            <h3>Select a conversation</h3>
            <p>Choose a conversation from the list or start a new one</p>
          </div>
        </div>

        <MessageThread
          v-else
          :conversation="messagesStore.activeConversation"
          :messages="messagesStore.messages"
          :loading="messagesStore.messagesLoading"
          :has-more-messages="messagesStore.hasMoreMessages"
          @load-more="loadMoreMessages"
          @manage-participants="showManageParticipants = true"
          @leave-conversation="handleLeaveConversation"
        />

        <!-- Mobile back button -->
        <button
          v-if="showThread && isMobile"
          class="mobile-back-btn"
          @click="goBackToList"
        >
          <i class="fa-solid fa-arrow-left"></i> Back
        </button>
      </div>
    </div>

    <!-- New Conversation Modal -->
    <NewConversationModal
      v-model="showNewConversationModal"
      @created="handleConversationCreated"
    />

    <!-- Manage Participants Modal (for group conversations) -->
    <AIVDialog
      v-model="showManageParticipants"
      title="Manage Participants"
      width="400px"
    >
      <div v-if="messagesStore.activeConversation" class="participants-management">
        <div class="current-participants">
          <h4>Current Participants</h4>
          <div
            v-for="participant in messagesStore.activeConversation.participants"
            :key="participant.id"
            class="participant-item"
          >
            <div class="participant-info">
              <div class="participant-avatar">
                <i class="fa-solid fa-user"></i>
              </div>
              <div class="participant-details">
                <span class="participant-name">
                  {{ participant.user?.first_name }} {{ participant.user?.last_name }}
                  <span v-if="participant.is_admin" class="admin-badge">Admin</span>
                </span>
                <span class="participant-type">{{ participant.user?.user_type }}</span>
              </div>
            </div>
            <button
              v-if="canRemoveParticipant(participant)"
              class="remove-participant-btn"
              @click="removeParticipant(participant.user?.id)"
              title="Remove participant"
            >
              <i class="fa-solid fa-times"></i>
            </button>
          </div>
        </div>

        <div class="add-participant-section">
          <el-button type="primary" round @click="showAddParticipants = true">
            <i class="fa-solid fa-plus"></i> Add Participants
          </el-button>
        </div>
      </div>

      <template slot="footer">
        <div class="text-center">
          <el-button type="primary" round @click="showManageParticipants = false">Done</el-button>
        </div>
      </template>
    </AIVDialog>

    <!-- Add Participants Modal -->
    <AIVDialog
      v-model="showAddParticipants"
      title="Add Participants"
      width="400px"
    >
      <div class="add-participants-modal">
        <div class="search-container">
          <i class="fa-solid fa-search search-icon"></i>
          <input
            v-model="userSearch"
            type="text"
            class="search-input"
            placeholder="Search users..."
            @input="searchUsers"
          />
        </div>

        <div class="users-list">
          <div v-if="loadingUsers" class="loading-state">
            <i class="fa-solid fa-spinner fa-spin"></i>
          </div>

          <div
            v-for="user in availableUsersToAdd"
            :key="user.id"
            :class="['user-item', { selected: selectedUsersToAdd.includes(user.id) }]"
            @click="toggleUserToAdd(user.id)"
          >
            <div class="user-avatar">
              <i class="fa-solid fa-user"></i>
            </div>
            <div class="user-info">
              <span class="user-name">{{ user.first_name }} {{ user.last_name }}</span>
              <span class="user-type">{{ user.user_type }}</span>
            </div>
            <i v-if="selectedUsersToAdd.includes(user.id)" class="fa-solid fa-check"></i>
          </div>
        </div>
      </div>

      <template slot="footer">
        <div class="modal-actions">
          <el-button @click="showAddParticipants = false">Cancel</el-button>
          <el-button
            type="primary"
            :disabled="selectedUsersToAdd.length === 0"
            :loading="addingParticipants"
            @click="addSelectedParticipants"
          >
            Add Selected
          </el-button>
        </div>
      </template>
    </AIVDialog>

    <!-- Leave Conversation Confirmation -->
    <AIVDialog
      v-model="showLeaveConfirmation"
      title="Leave Conversation"
      width="400px"
    >
      <div class="leave-confirmation">
        <p>Are you sure you want to leave this conversation?</p>
        <p v-if="messagesStore.activeConversation?.type === 'direct'" class="text-muted">
          You can start a new conversation with this person anytime.
        </p>
        <p v-else class="text-muted">
          You will no longer receive messages from this group.
        </p>
      </div>

      <template slot="footer">
        <div class="modal-actions">
          <el-button @click="showLeaveConfirmation = false">Cancel</el-button>
          <el-button
            type="danger"
            :loading="leavingConversation"
            @click="confirmLeaveConversation"
          >
            Leave Conversation
          </el-button>
        </div>
      </template>
    </AIVDialog>
  </div>
</template>

<script>
import ConversationList from "@/components/Messaging/ConversationList.vue";
import MessageThread from "@/components/Messaging/MessageThread.vue";
import NewConversationModal from "@/components/Messaging/NewConversationModal.vue";
import { useMessagesStore } from "@/stores/messagesStore";
import { useLoginStore } from "@/stores/loginStore";
import { usersService } from "@/serviceHandlers/usersService";
import { announcementsService } from "@/serviceHandlers/announcementsService";

export default {
  name: "Messages",
  components: {
    ConversationList,
    MessageThread,
    NewConversationModal,
  },
  data() {
    return {
      messagesStore: useMessagesStore(),
      loginStore: useLoginStore(),
      showNewConversationModal: false,
      showManageParticipants: false,
      showAddParticipants: false,
      showLeaveConfirmation: false,
      userSearch: "",
      allUsers: [],
      loadingUsers: false,
      selectedUsersToAdd: [],
      addingParticipants: false,
      leavingConversation: false,
      isMobile: false,
      showThread: false,
      // Announcements
      announcementsExpanded: false,
      announcements: [],
      loadingAnnouncements: false,
      sendingAnnouncement: false,
      newAnnouncement: {
        subject: "",
        body: "",
        delivery_method: "email",
        target_user_types: [],
      },
      userTypeOptions: [
        "Admin",
        //"Super Admin",
        "Lead",
        "Caregiver",
        "Employment Specialist",
        "Counselor",
        "Client",
      ],
      deliveryMethodOptions: [
        { id: "email", name: "Email" },
        { id: "sms", name: "SMS" },
        { id: "both", name: "Email & SMS" },
      ],
      userTypeSelectOptions: [
        { id: "Admin", name: "Admin" },
        { id: "Lead", name: "Lead" },
        { id: "Caregiver", name: "Caregiver" },
        { id: "Employment Specialist", name: "Employment Specialist" },
        { id: "Counselor", name: "Counselor" },
        { id: "Client", name: "Client" },
      ],
      announcementColumns: [
        { key: "subject", label: "Subject", sortable: true },
        { key: "senderName", label: "Sender", sortable: true },
        { key: "recipient_count", label: "Recipients", sortable: true },
        { key: "target_user_types", label: "User Types", sortable: false },
        { key: "delivery_method", label: "Method", sortable: true },
        { key: "sent_at", label: "Sent", sortable: true },
      ],
    };
  },
  computed: {
    recentAnnouncements() {
      return this.announcements.slice(0, 10);
    },
    canSendAnnouncement() {
      return (
        this.newAnnouncement.subject.trim() !== "" &&
        this.newAnnouncement.body.trim() !== "" &&
        this.newAnnouncement.target_user_types.length > 0
      );
    },
    availableUsersToAdd() {
      if (!this.messagesStore.activeConversation) return [];

      const currentParticipantIds = new Set(
        this.messagesStore.activeConversation.participants?.map((p) => p.user?.id) || []
      );
      const currentUserId = this.loginStore.user?.id;

      return this.allUsers.filter(
        (user) => user.id !== currentUserId && !currentParticipantIds.has(user.id)
      );
    },
  },
  watch: {
    "$route.query.userId": {
      immediate: true,
      handler(userId) {
        if (userId) {
          this.openDirectConversation(parseInt(userId, 10));
        }
      },
    },
  },
  async mounted() {
    // Check screen size
    this.checkMobile();
    window.addEventListener("resize", this.checkMobile);

    // Load conversations
    await this.messagesStore.loadConversations();
    await this.messagesStore.loadUnreadCount();

    // Load announcements for admins
    if (this.loginStore.isAdmin) {
      this.loadAnnouncements();
    }

    // Start polling
    this.messagesStore.startConversationPolling();

    // Handle direct conversation link
    const userId = this.$route.query.userId;
    if (userId) {
      await this.openDirectConversation(parseInt(userId, 10));
    }
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.checkMobile);
    this.messagesStore.cleanup();
  },
  methods: {
    async loadAnnouncements() {
      this.loadingAnnouncements = true;
      try {
        const response = await announcementsService.list();
        this.announcements = response || [];
      } catch {
      } finally {
        this.loadingAnnouncements = false;
      }
    },

    async sendAnnouncement() {
      if (!this.canSendAnnouncement) return;

      this.sendingAnnouncement = true;
      try {
        const response = await announcementsService.send(this.newAnnouncement);
        const recipientCount = response.recipientCount || 0;

        this.$notifySuccess(
          `<span>Announcement is being sent to ${recipientCount} recipient(s). You may safely navigate away from this page.</span>`
        );

        // Reset form
        this.newAnnouncement = {
          subject: "",
          body: "",
          delivery_method: "email",
          target_user_types: [],
        };

        // Reload announcements list
        await this.loadAnnouncements();
      } catch (error) {
        this.$notifyError("Failed to send announcement");
      } finally {
        this.sendingAnnouncement = false;
      }
    },

    checkMobile() {
      this.isMobile = window.innerWidth < 768;
    },

    async selectConversation(conversation) {
      await this.messagesStore.setActiveConversation(conversation.id);
      this.showThread = true;
    },

    goBackToList() {
      this.showThread = false;
      this.messagesStore.setActiveConversation(null);
    },

    async handleConversationCreated(conversation) {
      await this.messagesStore.setActiveConversation(conversation.id);
      this.showThread = true;
    },

    async openDirectConversation(userId) {
      try {
        const conversation = await this.messagesStore.getOrCreateDirectConversation(userId);
        await this.messagesStore.setActiveConversation(conversation.id);
        this.showThread = true;
        // Clear the query param
        this.$router.replace({ query: {} });
      } catch (error) {
        this.$notifyError("Failed to open conversation");
      }
    },

    loadMoreMessages() {
      this.messagesStore.loadMoreMessages();
    },

    handleLeaveConversation() {
      this.showLeaveConfirmation = true;
    },

    async confirmLeaveConversation() {
      if (!this.messagesStore.activeConversation) return;

      this.leavingConversation = true;
      try {
        await this.messagesStore.leaveConversation(this.messagesStore.activeConversation.id);
        this.showLeaveConfirmation = false;
        this.showThread = false;
        this.$notifySuccess("Left conversation");
      } catch (error) {
        this.$notifyError("Failed to leave conversation");
      } finally {
        this.leavingConversation = false;
      }
    },

    canRemoveParticipant(participant) {
      const activeConv = this.messagesStore.activeConversation;
      if (!activeConv || activeConv.type !== "group") return false;

      // Check if current user is admin
      const currentUserId = this.loginStore.user?.id;
      const currentUserParticipant = activeConv.participants?.find(
        (p) => p.user?.id === currentUserId
      );
      if (!currentUserParticipant?.is_admin) return false;

      // Cannot remove self or the creator
      if (participant.user?.id === currentUserId) return false;
      if (participant.user?.id === activeConv.created_by) return false;

      return true;
    },

    async removeParticipant(userId) {
      if (!userId || !this.messagesStore.activeConversation) return;

      try {
        await this.messagesStore.removeParticipant(
          this.messagesStore.activeConversation.id,
          userId
        );
        this.$notifySuccess("Participant removed");
      } catch (error) {
        this.$notifyError("Failed to remove participant");
      }
    },

    async searchUsers() {
      this.loadingUsers = true;
      try {
        const params = { activeStatus: "active" };
        if (this.userSearch.trim()) {
          params.search = this.userSearch.trim();
        }
        const response = await usersService.list(params);
        this.allUsers = response || [];
      } catch {
      } finally {
        this.loadingUsers = false;
      }
    },

    toggleUserToAdd(userId) {
      const index = this.selectedUsersToAdd.indexOf(userId);
      if (index === -1) {
        this.selectedUsersToAdd.push(userId);
      } else {
        this.selectedUsersToAdd.splice(index, 1);
      }
    },

    async addSelectedParticipants() {
      if (this.selectedUsersToAdd.length === 0) return;

      this.addingParticipants = true;
      try {
        await this.messagesStore.addParticipants(
          this.messagesStore.activeConversation.id,
          this.selectedUsersToAdd
        );
        this.showAddParticipants = false;
        this.selectedUsersToAdd = [];
        this.$notifySuccess("Participants added");
      } catch (error) {
        this.$notifyError("Failed to add participants");
      } finally {
        this.addingParticipants = false;
      }
    },
  },
};
</script>

<style scoped>
.messages-page {
  height: calc(100vh - 120px);
  display: flex;
  flex-direction: column;
}

.messages-layout {
  display: flex;
  flex: 1;
  gap: 20px;
  overflow: hidden;
}

.conversations-sidebar {
  width: 320px;
  flex-shrink: 0;
  overflow: hidden;
}

.thread-area {
  flex: 1;
  display: flex;
  flex-direction: column;
  position: relative;
  min-width: 0;
}

.no-conversation-selected {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.1);
  border-radius: 8px;
}

.empty-state {
  text-align: center;
  color: rgba(255, 255, 255, 0.6);
}

.empty-state i {
  font-size: 64px;
  margin-bottom: 20px;
  opacity: 0.4;
}

.empty-state h3 {
  margin: 0 0 8px 0;
  font-size: 20px;
  font-weight: 600;
  color: #fff;
}

.empty-state p {
  margin: 0 0 24px 0;
  font-size: 14px;
}

.mobile-back-btn {
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: 10;
  background: rgba(0, 0, 0, 0.5);
  border: none;
  color: #fff;
  padding: 8px 16px;
  border-radius: 20px;
  cursor: pointer;
  font-size: 14px;
}

/* Participants Management */
.participants-management {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.current-participants h4 {
  margin: 0 0 12px 0;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.7);
}

.participant-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  margin-bottom: 8px;
}

.participant-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.participant-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.6);
}

.participant-details {
  display: flex;
  flex-direction: column;
}

.participant-name {
  font-weight: 500;
  color: #fff;
  display: flex;
  align-items: center;
  gap: 8px;
}

.admin-badge {
  font-size: 10px;
  padding: 2px 6px;
  background: #6BA6CD;
  color: #fff;
  border-radius: 4px;
  font-weight: 600;
}

.participant-type {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
}

.remove-participant-btn {
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.4);
  cursor: pointer;
  padding: 4px 8px;
}

.remove-participant-btn:hover {
  color: #ff6b6b;
}

.add-participant-section {
  text-align: center;
  padding-top: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

/* Add Participants Modal */
.add-participants-modal {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.search-container {
  position: relative;
}

.search-icon {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(255, 255, 255, 0.4);
}

.search-input {
  width: 100%;
  padding: 12px 12px 12px 40px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.05);
  color: #fff;
  font-size: 14px;
}

.search-input:focus {
  outline: none;
  border-color: #6BA6CD;
}

.users-list {
  max-height: 250px;
  overflow-y: auto;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
}

.loading-state {
  padding: 24px;
  text-align: center;
  color: rgba(255, 255, 255, 0.5);
}

.user-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  cursor: pointer;
  transition: background-color 0.2s;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.user-item:last-child {
  border-bottom: none;
}

.user-item:hover {
  background: rgba(255, 255, 255, 0.05);
}

.user-item.selected {
  background: rgba(107, 166, 205, 0.15);
}

.user-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.6);
}

.user-info {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.user-name {
  font-weight: 500;
  color: #fff;
}

.user-type {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
}

/* Leave Confirmation */
.leave-confirmation {
  text-align: center;
  padding: 10px 0;
}

.leave-confirmation p {
  margin: 0 0 10px 0;
  color: rgba(255, 255, 255, 0.9);
}

.leave-confirmation .text-muted {
  color: rgba(255, 255, 255, 0.5);
  font-size: 14px;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

/* Announcements Section */
.announcements-section {
  margin-bottom: 12px;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.announcements-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  cursor: pointer;
  user-select: none;
}

.announcements-header:hover {
  background: rgba(255, 255, 255, 0.03);
  border-radius: 8px;
}

.announcements-title {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #fff;
  font-weight: 600;
  font-size: 15px;
}

.announcements-title .fa-bullhorn {
  color: #6BA6CD;
}

.announcements-header .fa-chevron-up,
.announcements-header .fa-chevron-down {
  color: rgba(255, 255, 255, 0.5);
  font-size: 13px;
}

.announcements-body {
  padding: 0 16px 16px;
  max-height: 400px;
  overflow-y: auto;
}

.announcement-form {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  padding: 14px;
  margin-bottom: 16px;
}

.announcement-form h4 {
  margin: 0 0 12px 0;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.7);
  font-weight: 600;
}

.form-row {
  margin-bottom: 10px;
}

.form-row:last-child {
  margin-bottom: 0;
}

.form-row-inline {
  display: flex;
  gap: 10px;
  align-items: flex-start;
}

.past-announcements h4 {
  margin: 0 0 10px 0;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.7);
  font-weight: 600;
}

/* Mobile Styles */
@media (max-width: 767px) {
  .messages-layout {
    gap: 0;
  }

  .conversations-sidebar {
    width: 100%;
  }

  .mobile-hidden {
    display: none !important;
  }

  .thread-area {
    width: 100%;
  }
}
</style>
